import React, { useState, useEffect } from 'react';
import { Car } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Vehicle Image Component - Displays vehicle image with fallback
 * 
 * @param {string} vehicleId - Vehicle ID to fetch image for
 * @param {string} brand - Vehicle brand (required for lookup and alt text)
 * @param {string} model - Vehicle model (required for lookup and alt text)
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} className - Additional CSS classes
 */
const VehicleImage = ({ 
  vehicleId, 
  brand, 
  model, 
  size = 'md',
  className = '' 
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  const sizes = {
    sm: { container: 'w-12 h-12', icon: 'w-6 h-6' },
    md: { container: 'w-16 h-16', icon: 'w-8 h-8' },
    lg: { container: 'w-24 h-24', icon: 'w-12 h-12' },
    xl: { container: 'w-32 h-32', icon: 'w-16 h-16' },
    '2xl': { container: 'w-48 h-32', icon: 'w-20 h-20' },
    'hero': { container: 'w-56 h-36', icon: 'w-16 h-16' }  // For VehicleDataPage header
  };
  
  const sizeConfig = sizes[size] || sizes.md;
  
  useEffect(() => {
    fetchVehicleImage();
  }, [vehicleId, brand, model]);
  
  const fetchVehicleImage = async () => {
    setLoading(true);
    setError(false);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Method 1: Try by vehicle ID first
      if (vehicleId) {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/vehicles/${vehicleId}/image-file`,
            { headers, responseType: 'blob' }
          );
          
          if (response.data && response.data.size > 0) {
            const blobUrl = URL.createObjectURL(response.data);
            setImageUrl(blobUrl);
            setLoading(false);
            return;
          }
        } catch (err) {
          // Continue to fallback methods
        }
      }
      
      // Method 2: Try by brand/model lookup
      if (brand && model) {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/vehicle-images/by-brand-model`,
            { 
              headers, 
              params: { brand, model },
              responseType: 'blob'
            }
          );
          
          if (response.data && response.data.size > 0) {
            const blobUrl = URL.createObjectURL(response.data);
            setImageUrl(blobUrl);
            setLoading(false);
            return;
          }
        } catch (err) {
          // Continue to fallback
        }
      }
      
      // No image found
      setError(true);
    } catch (err) {
      console.error('Failed to fetch vehicle image:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);
  
  return (
    <div 
      className={`rounded-xl overflow-hidden flex items-center justify-center ${sizeConfig.container} ${className}`}
      style={{ backgroundColor: 'rgba(30,160,230,0.1)' }}
    >
      {loading ? (
        <div className="animate-pulse bg-gray-700 w-full h-full" />
      ) : imageUrl && !error ? (
        <img
          src={imageUrl}
          alt={`${brand} ${model}`}
          className="w-full h-full object-contain"
          onError={() => setError(true)}
        />
      ) : (
        <Car className={sizeConfig.icon} style={{ color: '#7CD4FD' }} />
      )}
    </div>
  );
};

export default VehicleImage;
